import { oc } from "@orpc/contract";
import { z } from "zod";
import { RUNNER_PINS } from "../catalog-sidecar.js";
import { MODEL_FAMILIES } from "../models.js";
import { ENGINE_TARGETS, RuntimeProviderSchema } from "../runtime.js";
import { DELIVERIES, SEAT_CAPABILITIES } from "../seat-delivery.js";
import { LocaleSchema } from "../sites.js";
import { BUILTIN_TOOL_NAMES } from "../wire.js";
// The block schema (V6 plan row H.33; design headless/h4-block-configurators.md §B.5): what a
// configurator reads once to render its forms. The kinds with their JSON Schema, constraints,
// home and operations; the option lists (builtins, policies, graders, models, the MCP library,
// V5's skills, the runners); and one support row per served model pair and runner, computed by
// the checker's own rule (`seatDelivery`) from each model's profile and this deployment's
// facts. Public: no session, no key. Cached by `etag`, which changes with the release, a
// profile, or a deployment fact (risk G3), so a client re-reads when the etag changes.
//
// A support row is a hint for greying an option, never a verdict (risk G2): it answers one
// capability at a time, with the tool's policy unknown. Combined effects (a platform answer
// that then needs function calling, an approval gate on web search) show only in
// projects.validate. The option lists carry both site locales, so one cached answer serves
// every site; `locale` is accepted for parity with the other reads and changes nothing today.
//
// The kind names, the policy words and the operation names are the blocks subpath's
// (`@mfa/v5-contracts/blocks`: BLOCK_KINDS, TOOL_POLICIES, BlockOpSchema), which this root
// module does not load; they ride here as strings.
/** The capabilities a support row answers: the seat capabilities the checker reads, plus V5's own skills. */
export const SUPPORT_CAPABILITIES = [...SEAT_CAPABILITIES, "skills"];
/** One short label in each site locale. */
export const BlockLabelSchema = z.object({ en: z.string(), es: z.string() });
export const BlockKindDescriptionSchema = z.object({
    kind: z
        .string()
        .min(1)
        .describe("One of the block kinds (`@mfa/v5-contracts/blocks` BLOCK_KINDS)."),
    fields: z
        .record(z.string(), z.unknown())
        .describe("The kind's block as JSON Schema (draft-07): the shape projects.blocks returns for it."),
    constraints: z
        .record(z.string(), z.union([z.number(), z.string()]))
        .describe("The contracts constants that bound the kind, by name: counts, lengths, patterns."),
    home: z.array(z.object({
        document: z.enum(["agent", "overlay", "sidecar"]),
        pointer: z.string(),
    })),
    operations: z
        .array(z.string().min(1))
        .describe("The edit operations the kind accepts, by `op` (`@mfa/v5-contracts/blocks` BlockOpSchema)."),
});
export const BlockOptionsSchema = z.object({
    builtins: z.array(z.object({
        name: z.enum(BUILTIN_TOOL_NAMES),
        label: BlockLabelSchema,
        capability: z
            .enum(SUPPORT_CAPABILITIES)
            .describe("The capability a support row answers for this tool."),
    })),
    policies: z.array(z.object({
        policy: z.string().min(1).describe("A permission policy as agent.yaml writes it."),
        label: BlockLabelSchema,
        effect: z
            .record(z.enum(ENGINE_TARGETS), z.enum(["allow", "ask", "provider_decides"]))
            .describe("What the policy does on each runner when the tool is called."),
    })),
    graders: z.array(z.object({
        id: z.string().min(1),
        kind: z.enum(["code", "judge"]),
        paramKey: z.string().optional(),
        defaultParam: z.string().optional(),
        line: BlockLabelSchema.describe("The criterion line with the parameter as `{param}`."),
        label: BlockLabelSchema,
        description: BlockLabelSchema,
    })),
    models: z.array(z.object({
        ref: z
            .string()
            .min(1)
            .describe("What agent.yaml writes for the pair: bare at the model's home, prefixed elsewhere."),
        provider: RuntimeProviderSchema,
        canonical: z.string().min(1),
        native: z.string().min(1).describe("The name the provider knows the model by."),
        label: z.string().min(1),
        family: z.enum(MODEL_FAMILIES),
        home: z.boolean(),
    })),
    mcpLibrary: z.array(z.object({
        entryId: z.string().min(1),
        name: z.string().min(1),
        host: z
            .string()
            .min(1)
            .describe("The server URL's host only; the URL itself is mcpLibrary.list's."),
        active: z.boolean(),
    })),
    skills: z
        .array(z.object({ name: z.string().min(1), description: z.string() }))
        .describe("V5's own skills this deployment delivers into the sandbox."),
    runners: z.array(z.object({ runner: z.enum(RUNNER_PINS), label: BlockLabelSchema })),
});
export const BlockSupportAnswerSchema = z.object({
    delivery: z.enum(DELIVERIES),
    unmeasured: z
        .string()
        .optional()
        .describe("For a `none` caused by a profile key nobody measured: that key."),
});
export const BlockSupportRowSchema = z.object({
    provider: RuntimeProviderSchema,
    runner: z.enum(ENGINE_TARGETS),
    model: z.string().min(1).describe("The catalog id."),
    ref: z.string().min(1).describe("What agent.yaml writes for the pair (options.models[].ref)."),
    installed: z.boolean().describe("Whether this deployment takes a run on the pair now."),
    held: z
        .boolean()
        .describe("The provider's work is held (decision 24): read-only, nothing new runs."),
    profile: z
        .object({
        probedOn: z.string(),
        stale: z.boolean().describe("Probed longer ago than the checker's profile age limit."),
    })
        .nullable()
        .describe("The model's capability profile on this provider; null where none is needed or none is loaded."),
    capabilities: z.record(z.enum(SUPPORT_CAPABILITIES), BlockSupportAnswerSchema),
    serviceFacts: z
        .object({
        webSearch: z.boolean(),
        mcp: z.boolean(),
        customTools: z.boolean(),
        teams: z.boolean(),
    })
        .optional()
        .describe("On the sandbox service: what the worker runs, as the runner rule reads it."),
});
export const BlocksSchemaResultSchema = z.object({
    blocksVersion: z.number().int().positive(),
    etag: z.string().min(1),
    kinds: z.array(BlockKindDescriptionSchema),
    options: BlockOptionsSchema,
    support: z.array(BlockSupportRowSchema),
});
export const blocks = {
    schema: oc
        .route({
        summary: "The block schema",
        description: "What a configurator reads once to render its forms: every block kind with its JSON Schema, constraints, home and edit operations; the option lists (the builtin tools and the capability each needs, the permission policies and what each does on each runner, the grader catalog as data with `{param}` for the parameter, the served model pairs, the MCP library as id, name and host, V5's own skills, the runner pins); and one support row per served model pair and runner, computed by the checker's seat rule from the model's profile and this deployment's facts. A support row is a hint for greying an option, never a verdict: it answers one capability at a time, and the verdict on a whole candidate is projects.validate's. Public, no credential needed. Cached by `etag` (also the weak ETag header), which changes with the release, a profile or a deployment fact. Labels carry both locales; `locale` changes nothing today.",
    })
        .meta({ access: "read", public: true })
        .input(z.object({ locale: LocaleSchema.optional() }))
        .output(BlocksSchemaResultSchema),
};
//# sourceMappingURL=blocks.js.map